import Rioglyph from '@rio-cloud/rio-uikit/Rioglyph';
import { FormattedMessage } from 'react-intl';
import {
    TransportAssignmentBulkUpdatePreviewError,
    TransportAssignmentBulkUpdatePreviewErrorCode,
    TransportAssignmentBulkUpdatePreviewResult,
} from '../transportAssignment.types';
import { uniq } from 'es-toolkit';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import {
    BulkStatusError,
    BulkStatusErrorMessage,
    getDefaultBulkStatusErrorMessageId,
} from '../../sharedComponents/bulkStatusUpdate/BulkStatusErrorMessage';
import { getTransportAssignmentBulkSelection } from '../../reducers/transportAssignmentsSlice';
import { MAX_BULK_SELECTION_SIZE } from './TransportOrderBulkUpdate';
import { BulkUpdateMaxSizeErrorMessage } from '../../sharedComponents/bulkStatusUpdate/BulkUpdateMaxSizeErrorMessage';

type TransportOrderBulkUpdateValidationFeedbackProps = {
    readonly previewResult: TransportAssignmentBulkUpdatePreviewResult;
};

// eslint-disable-next-line max-lines-per-function
export const TransportOrderBulkUpdateValidationFeedback = ({
    previewResult,
}: TransportOrderBulkUpdateValidationFeedbackProps) => {
    const selectedIds = useAppSelector(getTransportAssignmentBulkSelection);

    let feedback;
    if (!previewResult.success) {
        const selectedIdsCount = selectedIds?.length ?? 0;

        switch (previewResult.errorCode) {
            case 'size-exceeded':
                feedback = (
                    <BulkUpdateMaxSizeErrorMessage
                        totalCount={selectedIdsCount}
                        maxBulkSize={MAX_BULK_SELECTION_SIZE}
                    />
                );
                break;
            case 'duplicate-vin':
                feedback = (
                    <>
                        <div>
                            <Rioglyph icon="rioglyph-remove-sign" iconClassName="text-color-danger" size="h1" />
                        </div>
                        <div>
                            <FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.failure.duplicateVin" />
                        </div>
                    </>
                );
                break;
        }
    } else {
        const validAssignmentsCount = previewResult.assignments.length;
        const invalidAssignments = previewResult.invalidAssignments;
        const uniqueInvalidAssignmentsCount = uniq(invalidAssignments.flatMap((error) => error.assignments)).length;
        const totalAssignmentsCount = validAssignmentsCount + uniqueInvalidAssignmentsCount;

        if (validAssignmentsCount === 0) {
            feedback = (
                <>
                    <div>
                        <Rioglyph icon="rioglyph-remove-sign" iconClassName="text-color-danger" size="h1" />
                    </div>
                    <div>
                        <FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.failure" />
                    </div>
                    {invalidAssignments.length > 0 && (
                        <BulkStatusErrorMessage
                            errors={mapOrderErrors(invalidAssignments)}
                            getErrorMessageId={getTransportAssignmentErrorMessageId}
                        />
                    )}
                </>
            );
        } else {
            feedback = (
                <>
                    <div>
                        <Rioglyph icon="rioglyph-check-badge" iconClassName="text-color-success" size="h1" />
                    </div>
                    <div>
                        <FormattedMessage
                            id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.successCount"
                            values={{ successCount: validAssignmentsCount, totalCount: totalAssignmentsCount }}
                        />
                    </div>
                    {invalidAssignments.length > 0 && (
                        <BulkStatusErrorMessage
                            errors={mapOrderErrors(invalidAssignments)}
                            getErrorMessageId={getTransportAssignmentErrorMessageId}
                        />
                    )}
                </>
            );
        }
    }

    return (
        <div className="min-height-300 display-flex flex-column justify-content-center align-items-center gap-20">
            <div className="text-size-16">
                <FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.title" />
            </div>
            {feedback}
        </div>
    );
};

const mapOrderErrors = (
    invalidAssignments: TransportAssignmentBulkUpdatePreviewError[],
): BulkStatusError<TransportAssignmentBulkUpdatePreviewErrorCode>[] =>
    invalidAssignments.map((error) => ({
        errorCode: error.errorCode,
        identifiers: error.assignments,
        identifierType: 'ORDER' as const,
    }));

const getTransportAssignmentErrorMessageId = (errorCode: TransportAssignmentBulkUpdatePreviewErrorCode) =>
    getDefaultBulkStatusErrorMessageId(errorCode);
